import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArticleRounded,
  AssignmentTurnedInRounded,
  CheckCircleOutlineRounded,
  CheckCircleRounded,
  CloseRounded,
  DoneAllRounded,
  DownloadRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  GradingRounded,
  LockRounded,
  MenuBookRounded,
  OpenInNewRounded,
  PlayCircleRounded,
  QuizRounded,
  RadioButtonUncheckedRounded,
  UnfoldLessRounded,
  UnfoldMoreRounded,
  UploadFileRounded,
} from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTenantStore } from '../../../stores/tenant.store';
import { useStudentIdentity } from '../../../hooks/use-student-identity';
import { courseMaterialApi } from '../../../lib/api/course-material.api';
import { lmsApi, type PageDto, type StructureItemDto } from '../../../lib/api/lms.api';
import { userItemViewApi } from '../../../lib/api/user-item-view.api';
import { useSectionItemViews, useSectionStructure } from '../hooks/use-portal-data';
import { PortalEmpty, PortalLoading } from '../PortalShell';
import { courseColor } from './CourseShell';
import { ScriptureRefText } from '../scripture/reference-linker';
import { isPlayableVideoFile, toEmbedUrl } from '../util/video-embed';
import { formatDate } from '../../../lib/i18n-format';
import { usePortalViewing } from '../../../stores/portal-view.store';

function itemIcon(item: StructureItemDto) {
  if (item.type === 'page') return <ArticleRounded fontSize="small" />;
  if (item.type === 'assignment') {
    return item.assignment?.isQuiz ? (
      <QuizRounded fontSize="small" />
    ) : (
      <AssignmentTurnedInRounded fontSize="small" />
    );
  }
  const mt = item.material?.materialType;
  if (mt === 'video') return <PlayCircleRounded fontSize="small" />;
  if (mt === 'reading') return <MenuBookRounded fontSize="small" />;
  return <ArticleRounded fontSize="small" />;
}

/** An assignment item counts as "done" once the student has submitted (or it's
 *  already graded) — NOT when merely viewed. 'returned' (sent back for revision)
 *  is not done. */
function isAssignmentSubmitted(status?: string): boolean {
  return status === 'submitted' || status === 'graded';
}

/** True when an item is completed: assignments derive from their submission,
 *  everything else from the per-item view record. */
function isItemDone(
  item: StructureItemDto,
  views: Record<string, { completedAt?: string | null }>,
) {
  if (item.type === 'assignment') return isAssignmentSubmitted(item.assignment?.submissionStatus);
  return !!views[item.id]?.completedAt;
}

/** Short due-date label like "vie 22 may" in the tenant-agnostic es-CL locale. */
function dueLabel(dueAt?: string): string | null {
  if (!dueAt) return null;
  return formatDate(dueAt, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/** Student submission status → chip label (i18n key + fallback) + color. */
const ASSIGNMENT_STATUS: Record<
  string,
  { key: string; fallback: string; color: 'default' | 'success' | 'warning' | 'info' }
> = {
  none: { key: 'portal.modules.status.pending', fallback: 'Pendiente', color: 'warning' },
  draft: { key: 'portal.modules.status.draft', fallback: 'Borrador', color: 'default' },
  submitted: { key: 'portal.modules.status.submitted', fallback: 'Entregada', color: 'info' },
  graded: { key: 'portal.modules.status.graded', fallback: 'Calificada', color: 'success' },
  returned: { key: 'portal.modules.status.returned', fallback: 'Devuelta', color: 'warning' },
};

function isModuleLocked(releaseAt?: string): boolean {
  if (!releaseAt) return false;
  return new Date(releaseAt) > new Date();
}

export function CourseModulesPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { sectionId } = useParams<{ sectionId: string }>();
  const { currentTenantId } = useTenantStore();
  const { personId } = useStudentIdentity();
  const navigate = useNavigate();
  // «Ver portal»: se mira el curso como el alumno, sin escribir nada a su nombre
  // —ni «visto», ni «completado», ni el estado plegado en el navegador— y sin
  // abrir el detalle de una tarea (fuera de la versión 1).
  const viewing = usePortalViewing();
  const qc = useQueryClient();
  const { data, isLoading } = useSectionStructure(sectionId);
  const viewsQ = useSectionItemViews(sectionId);
  const [openItem, setOpenItem] = useState<StructureItemDto | null>(null);

  // Optimistic overlay applied on top of server state for instant feedback.
  type ViewState = { viewedAt?: string; completedAt?: string | null };
  const [optimistic, setOptimistic] = useState<Record<string, ViewState>>({});

  // Per-section collapsed-module set, persisted in localStorage so the
  // student's preference survives reloads. Default: all expanded.
  const collapseStorageKey = sectionId ? `portal-v2:modules-collapsed:${sectionId}` : null;
  const [collapsedModules, setCollapsedModules] = useState<Set<string>>(() => {
    if (typeof window === 'undefined' || !collapseStorageKey) return new Set();
    try {
      const raw = window.localStorage.getItem(collapseStorageKey);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return new Set(parsed as string[]);
    } catch {
      /* ignore */
    }
    return new Set();
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !collapseStorageKey || viewing) return;
    try {
      window.localStorage.setItem(collapseStorageKey, JSON.stringify([...collapsedModules]));
    } catch {
      /* ignore */
    }
  }, [collapsedModules, collapseStorageKey, viewing]);
  const toggleModuleCollapse = (moduleId: string) => {
    setCollapsedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) next.delete(moduleId);
      else next.add(moduleId);
      return next;
    });
  };
  const collapseAll = (moduleIds: string[]) => setCollapsedModules(new Set(moduleIds));
  const expandAll = () => setCollapsedModules(new Set());

  const views = useMemo(() => {
    const map: Record<string, ViewState> = {};
    for (const v of viewsQ.data?.items ?? []) {
      map[v.itemId] = { viewedAt: v.viewedAt, completedAt: v.completedAt };
    }
    return { ...map, ...optimistic };
  }, [viewsQ.data, optimistic]);

  const refreshViews = () => qc.invalidateQueries({ queryKey: ['portal-v2-item-views'] });

  const markViewed = async (itemId: string) => {
    if (!currentTenantId || !sectionId || viewing) return;
    // Optimistic: mark viewed immediately
    setOptimistic((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], viewedAt: new Date().toISOString() },
    }));
    try {
      await userItemViewApi.markViewed({
        tenantId: currentTenantId,
        itemId,
        sectionId,
      });
      refreshViews();
    } catch {
      // Rollback optimistic mark on failure
      setOptimistic((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    }
  };

  const setCompleted = async (itemId: string, completed: boolean) => {
    if (!currentTenantId || !sectionId || viewing) return;
    const previous = views[itemId];
    // Optimistic flip
    setOptimistic((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        viewedAt: prev[itemId]?.viewedAt ?? new Date().toISOString(),
        completedAt: completed ? new Date().toISOString() : null,
      },
    }));
    try {
      await userItemViewApi.setCompleted({
        tenantId: currentTenantId,
        itemId,
        sectionId,
        completed,
      });
      refreshViews();
    } catch {
      // Rollback on failure
      setOptimistic((prev) => {
        const next = { ...prev };
        if (previous) next[itemId] = previous;
        else delete next[itemId];
        return next;
      });
    }
  };

  // Storage-backed file download. Must run inside the click gesture: the
  // download URL is fetched async, and a popup blocker would kill a
  // window.open() called after the await. So open a blank tab synchronously
  // now and point it at the signed URL once it resolves.
  const downloadFile = (item: StructureItemDto) => {
    if (!currentTenantId) return;
    const win = window.open('about:blank', '_blank');
    markViewed(item.id);
    courseMaterialApi
      .getDownloadUrl({
        tenantId: currentTenantId,
        materialId: item.targetId,
        ...(personId ? { requestingStudentId: personId } : {}),
      })
      .then(({ url }) => {
        if (win) win.location.href = url;
        else window.location.href = url;
        setCompleted(item.id, true);
      })
      .catch(() => {
        win?.close();
      });
  };

  const markAllDone = async (itemIds: string[]) => {
    if (!currentTenantId || !sectionId || itemIds.length === 0 || viewing) return;
    const pending = itemIds.filter((id) => !views[id]?.completedAt);
    if (pending.length === 0) return;
    const nowIso = new Date().toISOString();
    setOptimistic((prev) => {
      const next = { ...prev };
      for (const id of pending) {
        next[id] = {
          viewedAt: next[id]?.viewedAt ?? nowIso,
          completedAt: nowIso,
        };
      }
      return next;
    });
    try {
      await Promise.all(
        pending.map((itemId) =>
          userItemViewApi.setCompleted({
            tenantId: currentTenantId,
            itemId,
            sectionId,
            completed: true,
          }),
        ),
      );
      refreshViews();
    } catch {
      // Best effort: refresh from server to reconcile
      refreshViews();
    }
  };

  if (isLoading) return <PortalLoading />;
  if (!data || data.modules.length === 0) {
    return (
      <PortalEmpty
        icon={<MenuBookRounded fontSize="inherit" />}
        title={t('portal.modules.emptyTitle')}
        body={t('portal.modules.emptyBody')}
      />
    );
  }

  const color = sectionId ? courseColor(sectionId) : '#6366f1';
  const visibleModules = data.modules.filter((m) => m.publishedAt);
  const allCollapsed =
    visibleModules.length > 0 && visibleModules.every((m) => collapsedModules.has(m.id));

  return (
    <Stack spacing={2}>
      {visibleModules.length > 1 && (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
          <Typography variant="caption" color="#6e7681">
            {t('portal.modules.expandedCount', {
              count: visibleModules.length - collapsedModules.size,
              total: visibleModules.length,
            })}
          </Typography>
          <Button
            size="small"
            startIcon={
              allCollapsed ? (
                <UnfoldMoreRounded sx={{ fontSize: 16 }} />
              ) : (
                <UnfoldLessRounded sx={{ fontSize: 16 }} />
              )
            }
            onClick={() =>
              allCollapsed ? expandAll() : collapseAll(visibleModules.map((m) => m.id))
            }
            sx={{
              color: '#8b949e',
              textTransform: 'none',
              fontSize: 12,
              '&:hover': { color: '#e6edf3', bgcolor: 'rgba(255,255,255,0.04)' },
            }}
          >
            {allCollapsed ? t('portal.modules.expandAll') : t('portal.modules.collapseAll')}
          </Button>
        </Stack>
      )}
      {visibleModules.map((m, mIdx) => {
        const locked = isModuleLocked(m.releaseAt);
        const items = m.items.filter((i) => i.publishedAt);
        const completedCount = items.filter((i) => isItemDone(i, views)).length;
        const progressPct =
          items.length === 0 ? 0 : Math.round((completedCount / items.length) * 100);
        // Assignment sub-summary for the module header ("N tareas · M pendientes").
        const assignmentItems = items.filter((i) => i.type === 'assignment');
        const pendingAssignments = assignmentItems.filter(
          (i) => !isAssignmentSubmitted(i.assignment?.submissionStatus),
        ).length;
        return (
          <Card
            key={m.id}
            sx={{
              bgcolor: '#161b22',
              border: '1px solid #21262d',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box
              onClick={() => toggleModuleCollapse(m.id)}
              sx={{
                px: 2.5,
                py: 1.75,
                borderBottom: collapsedModules.has(m.id) ? 'none' : '1px solid #21262d',
                cursor: 'pointer',
                transition: 'background 0.15s',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
              }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    bgcolor: color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 13,
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  {mIdx + 1}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700} color="#e6edf3">
                    {m.title}
                  </Typography>
                  {m.description && (
                    <Typography variant="caption" color="#8b949e" display="block" sx={{ mt: 0.25 }}>
                      {m.description}
                    </Typography>
                  )}
                </Box>
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/portal-v2/courses/${sectionId}/clases`);
                  }}
                  sx={{ textTransform: 'none', color: '#8b949e', fontSize: 12, flexShrink: 0 }}
                >
                  {t('portal.modules.viewClasses', { defaultValue: 'Clases →' })}
                </Button>
                <Tooltip
                  title={
                    collapsedModules.has(m.id)
                      ? t('portal.modules.expand')
                      : t('portal.modules.collapse')
                  }
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleModuleCollapse(m.id);
                    }}
                    sx={{ color: '#8b949e', mt: -0.5, mr: -0.5 }}
                  >
                    {collapsedModules.has(m.id) ? <ExpandMoreRounded /> : <ExpandLessRounded />}
                  </IconButton>
                </Tooltip>
              </Stack>
              {(locked || items.length > 0) && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 1.25, pl: { xs: 0, sm: 5 } }}
                >
                  {locked ? (
                    <Chip
                      size="small"
                      icon={<LockRounded sx={{ fontSize: 14 }} />}
                      label={t('portal.modules.availableOn', {
                        date: formatDate(m.releaseAt!, {
                          day: 'numeric',
                          month: 'short',
                        }),
                      })}
                      color="default"
                      variant="outlined"
                    />
                  ) : (
                    <>
                      <Chip
                        size="small"
                        label={`${completedCount}/${items.length} · ${progressPct}%`}
                        color={progressPct === 100 ? 'success' : 'primary'}
                        variant="outlined"
                      />
                      {assignmentItems.length > 0 && (
                        <Chip
                          size="small"
                          icon={<AssignmentTurnedInRounded sx={{ fontSize: 14 }} />}
                          label={
                            pendingAssignments > 0
                              ? t('portal.modules.assignmentsPending', {
                                  count: assignmentItems.length,
                                  pending: pendingAssignments,
                                  defaultValue: `${assignmentItems.length} tareas · ${pendingAssignments} pendiente(s)`,
                                })
                              : t('portal.modules.assignmentsDone', {
                                  count: assignmentItems.length,
                                  defaultValue: `${assignmentItems.length} tareas · al día`,
                                })
                          }
                          color={pendingAssignments > 0 ? 'warning' : 'success'}
                          variant="outlined"
                        />
                      )}
                      {!viewing &&
                        items.some((i) => i.type !== 'assignment' && !views[i.id]?.completedAt) && (
                          <Button
                            size="small"
                            startIcon={<DoneAllRounded sx={{ fontSize: 16 }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Assignments are completed by submitting, not by a manual mark.
                              markAllDone(
                                items.filter((i) => i.type !== 'assignment').map((i) => i.id),
                              );
                            }}
                            sx={{
                              color: '#8b949e',
                              textTransform: 'none',
                              fontSize: 12,
                              py: 0,
                              '&:hover': { color: '#e6edf3', bgcolor: 'rgba(255,255,255,0.04)' },
                            }}
                          >
                            {t('portal.modules.markAll')}
                          </Button>
                        )}
                    </>
                  )}
                </Stack>
              )}
            </Box>

            <Collapse in={!collapsedModules.has(m.id)} unmountOnExit>
              <Box>
                {locked ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <LockRounded sx={{ color: '#6e7681', fontSize: 36, mb: 1 }} />
                    <Typography variant="body2" color="#8b949e">
                      {t('portal.modules.unlocksOn', {
                        date: formatDate(m.releaseAt!, {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                        }),
                      })}
                    </Typography>
                  </Box>
                ) : items.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="caption" color="#8b949e">
                      {t('portal.modules.noContent')}
                    </Typography>
                  </Box>
                ) : (
                  items.map((item) => {
                    const isAssignment = item.type === 'assignment';
                    const done = isItemDone(item, views);
                    // Assignments never show the "new" muting; they carry their own
                    // status chip instead.
                    const isViewed = isAssignment ? true : !!views[item.id]?.viewedAt;
                    const sub = item.assignment?.submissionStatus ?? 'none';
                    const due = dueLabel(item.assignment?.dueAt);
                    const statusChip = ASSIGNMENT_STATUS[sub] ?? ASSIGNMENT_STATUS['none']!;
                    return (
                      <Box
                        key={item.id}
                        onClick={() => {
                          if (isAssignment) {
                            if (viewing) return;
                            markViewed(item.id);
                            navigate(
                              `/portal-v2/courses/${sectionId}/assignments/${item.targetId}`,
                            );
                            return;
                          }
                          if (item.type === 'material' && item.material?.storagePath) {
                            downloadFile(item);
                            return;
                          }
                          markViewed(item.id);
                          setOpenItem(item);
                        }}
                        sx={{
                          px: 2.5,
                          py: 1.5,
                          pl: 2.5 + item.indent * 3,
                          borderBottom: '1px solid #21262d',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          cursor: viewing && isAssignment ? 'default' : 'pointer',
                          transition: 'background 0.15s',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                          '&:last-child': { borderBottom: 'none' },
                        }}
                      >
                        {isAssignment || viewing ? (
                          // Completion is driven by the submission, not a manual mark
                          // (and in a portal view, it is only shown).
                          <Box
                            sx={{
                              display: 'flex',
                              width: 34,
                              justifyContent: 'center',
                              color: done ? '#10b981' : '#6e7681',
                            }}
                          >
                            {done ? (
                              <CheckCircleRounded fontSize="small" />
                            ) : (
                              <RadioButtonUncheckedRounded fontSize="small" />
                            )}
                          </Box>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCompleted(item.id, !done);
                            }}
                            sx={{ color: done ? '#10b981' : '#6e7681' }}
                          >
                            {done ? (
                              <CheckCircleRounded fontSize="small" />
                            ) : (
                              <RadioButtonUncheckedRounded fontSize="small" />
                            )}
                          </IconButton>
                        )}
                        <Box sx={{ color: isViewed ? '#8b949e' : '#c9d1d9' }}>{itemIcon(item)}</Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: isViewed ? '#8b949e' : '#e6edf3',
                              fontWeight: isViewed ? 400 : 500,
                            }}
                          >
                            {item.title}
                          </Typography>
                          {isAssignment && due && (
                            <Typography variant="caption" color="#8b949e" display="block">
                              {t('portal.modules.dueOn', {
                                date: due,
                                defaultValue: `Entrega: ${due}`,
                              })}
                            </Typography>
                          )}
                        </Box>
                        {!isAssignment && !isViewed && (
                          <Chip
                            size="small"
                            label={t('portal.modules.new')}
                            sx={{
                              height: 18,
                              fontSize: 10,
                              bgcolor: 'rgba(99,102,241,0.15)',
                              color: '#a5b4fc',
                              border: 'none',
                            }}
                          />
                        )}
                        {item.type === 'material' && item.material?.storagePath && (
                          <Chip
                            size="small"
                            icon={<DownloadRounded sx={{ fontSize: 14 }} />}
                            label={t('portal.modules.file')}
                            variant="outlined"
                            sx={{ height: 20, fontSize: 11, borderColor: '#30363d' }}
                          />
                        )}
                        {isAssignment && (
                          <>
                            <Chip
                              size="small"
                              label={t(statusChip.key, { defaultValue: statusChip.fallback })}
                              color={statusChip.color}
                              variant="outlined"
                              sx={{ height: 20, fontSize: 11 }}
                            />
                            <Chip
                              size="small"
                              icon={
                                sub === 'graded' ? (
                                  <GradingRounded sx={{ fontSize: 14 }} />
                                ) : item.assignment?.isQuiz ? (
                                  <QuizRounded sx={{ fontSize: 14 }} />
                                ) : (
                                  <UploadFileRounded sx={{ fontSize: 14 }} />
                                )
                              }
                              label={
                                sub === 'graded'
                                  ? t('portal.modules.ctaViewGrade', { defaultValue: 'Ver nota' })
                                  : item.assignment?.isQuiz
                                    ? t('portal.modules.ctaQuiz', { defaultValue: 'Responder' })
                                    : t('portal.modules.ctaSubmit', { defaultValue: 'Entregar' })
                              }
                              sx={{
                                height: 20,
                                fontSize: 11,
                                bgcolor: 'rgba(245,158,11,0.15)',
                                color: '#fbbf24',
                                border: 'none',
                              }}
                            />
                          </>
                        )}
                      </Box>
                    );
                  })
                )}
              </Box>
            </Collapse>
          </Card>
        );
      })}

      <ItemDialog
        item={openItem}
        onClose={() => setOpenItem(null)}
        onCompleted={(itemId) => setCompleted(itemId, true)}
      />
    </Stack>
  );
}

function ItemDialog({
  item,
  onClose,
  onCompleted,
}: {
  item: StructureItemDto | null;
  sectionId?: string | undefined;
  onClose: () => void;
  onCompleted: (itemId: string) => void;
}): JSX.Element | null {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const { personId } = useStudentIdentity();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [page, setPage] = useState<PageDto | null>(null);
  const [loading, setLoading] = useState(false);

  const isPage = item?.type === 'page';
  const isVideoMaterial = item?.type === 'material' && item.material?.materialType === 'video';
  const isExternalNonVideoMaterial =
    item?.type === 'material' && !!item.material?.url && item.material.materialType !== 'video';

  useEffect(() => {
    if (!item || !currentTenantId) return;
    if (isPage) {
      setLoading(true);
      lmsApi
        .getPage({
          tenantId: currentTenantId,
          pageId: item.targetId,
          ...(personId ? { requestingStudentId: personId } : {}),
        })
        .then(setPage)
        .finally(() => setLoading(false));
      return;
    }
    // Video URL → keep dialog open and render embedded player below.
    if (isVideoMaterial && item.material?.url) {
      onCompleted(item.id);
      return;
    }
    // Non-video external link (reading / notes) → still open in new tab.
    if (isExternalNonVideoMaterial && item.material?.url) {
      window.open(item.material.url, '_blank', 'noopener,noreferrer');
      onCompleted(item.id);
      onClose();
      return;
    }
    // Storage-backed files are downloaded directly from the row click in
    // CourseModulesPage (popup-blocker-safe); they never open this dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  if (!item) return null;
  if (!isPage && !isVideoMaterial) return null;

  const embed = isVideoMaterial && item.material?.url ? toEmbedUrl(item.material.url) : null;
  const directVideoFile =
    isVideoMaterial && item.material?.url && isPlayableVideoFile(item.material.url);
  const videoUrl = item.material?.url;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={isVideoMaterial ? 'lg' : 'md'}
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { bgcolor: '#0d1117' } }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #21262d' }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" color="#e6edf3" fontWeight={700}>
            {page?.title ?? item.title}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#8b949e' }}>
          <CheckCircleOutlineRounded fontSize="small" sx={{ mr: 0.5 }} />
          <Typography variant="caption" sx={{ mr: 1 }}>
            {t('portal.modules.markRead')}
          </Typography>
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 3 }}>
        {isPage &&
          (loading ? (
            <Box textAlign="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ color: '#c9d1d9' }}>
              <PageRenderer body={page?.bodyJson} />
            </Box>
          ))}
        {isVideoMaterial && videoUrl && (
          <VideoPlayer
            url={videoUrl}
            embed={embed}
            directVideoFile={!!directVideoFile}
            description={item.material?.description}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function VideoPlayer({
  url,
  embed,
  directVideoFile,
  description,
}: {
  url: string;
  embed: ReturnType<typeof toEmbedUrl>;
  directVideoFile: boolean;
  description?: string | undefined;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const openInNewTab = (
    <Button
      size="small"
      startIcon={<OpenInNewRounded />}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ color: '#a5b4fc', textTransform: 'none', mt: 1 }}
    >
      {t('portal.modules.openInNewTab')}
    </Button>
  );

  let player: JSX.Element;
  if (directVideoFile) {
    player = (
      <Box
        component="video"
        src={url}
        controls
        sx={{ width: '100%', maxHeight: '70vh', borderRadius: 1, bgcolor: '#000' }}
      />
    );
  } else if (embed) {
    player = (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingTop: `${(1 / (embed.aspectRatio ?? 16 / 9)) * 100}%`,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: '#000',
        }}
      >
        <Box
          component="iframe"
          src={embed.src}
          {...(embed.allow ? { allow: embed.allow } : {})}
          allowFullScreen
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 0,
          }}
        />
      </Box>
    );
  } else {
    player = (
      <Box
        sx={{
          bgcolor: '#161b22',
          border: '1px solid #21262d',
          borderRadius: 1,
          p: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="#8b949e" mb={2}>
          {t('portal.modules.videoNotPlayable')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<OpenInNewRounded />}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('portal.modules.openVideo')}
        </Button>
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      {player}
      {description && (
        <Typography variant="body2" color="#c9d1d9" sx={{ whiteSpace: 'pre-wrap' }}>
          {description}
        </Typography>
      )}
      {(directVideoFile || embed) && openInNewTab}
    </Stack>
  );
}

function PageRenderer({ body }: { body?: Record<string, unknown> | undefined }): JSX.Element {
  const { t } = useTranslation('sis');
  if (!body) return <Typography color="#8b949e">{t('portal.modules.noPageContent')}</Typography>;
  const root = body as { content?: Array<Record<string, unknown>> };
  return <Stack spacing={1.5}>{(root.content ?? []).map((node, i) => renderNode(node, i))}</Stack>;
}

function renderNode(node: Record<string, unknown>, key: number): JSX.Element | null {
  const type = node['type'] as string;
  const content = node['content'] as Array<Record<string, unknown>> | undefined;
  const attrs = (node['attrs'] ?? {}) as Record<string, unknown>;
  if (type === 'paragraph') {
    return (
      <Typography key={key} variant="body2" sx={{ lineHeight: 1.6 }}>
        {(content ?? []).map((c, i) => renderInline(c, i))}
      </Typography>
    );
  }
  if (type === 'heading') {
    const level = (attrs['level'] as number) ?? 2;
    const fontSize = level === 1 ? 22 : level === 2 ? 18 : 16;
    return (
      <Typography key={key} sx={{ fontWeight: 700, fontSize, color: '#e6edf3', mt: 1 }}>
        {(content ?? []).map((c, i) => renderInline(c, i))}
      </Typography>
    );
  }
  if (type === 'bulletList' || type === 'orderedList') {
    const items = content ?? [];
    return (
      <Box key={key} component={type === 'bulletList' ? 'ul' : 'ol'} sx={{ pl: 3, my: 0.5 }}>
        {items.map((li, i) => (
          <Box component="li" key={i} sx={{ mb: 0.5 }}>
            <Typography variant="body2">
              {((li['content'] as Array<Record<string, unknown>>) ?? []).flatMap((p, j) =>
                ((p['content'] as Array<Record<string, unknown>>) ?? []).map((c, k) =>
                  renderInline(c, j * 100 + k),
                ),
              )}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  return null;
}

function renderInline(node: Record<string, unknown>, key: number): JSX.Element | string {
  if (node['type'] === 'text') {
    const text = (node['text'] as string) ?? '';
    const marks = (node['marks'] as Array<{ type: string }> | undefined) ?? [];
    let el: JSX.Element = (
      <span key={`s-${key}`}>
        <ScriptureRefText>{text}</ScriptureRefText>
      </span>
    );
    if (marks.some((m) => m.type === 'bold'))
      el = (
        <strong key={`bold-${key}`} style={{ fontWeight: 700 }}>
          {el}
        </strong>
      );
    if (marks.some((m) => m.type === 'italic')) el = <em key={`em-${key}`}>{el}</em>;
    return el;
  }
  return '';
}
